import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createOrganizationInvitesQueryKey } from '@/utils/queryKeys';
import type {
  ResendOrganizationInviteMutation,
  ResendOrganizationInviteMutationVariables,
} from './useResendOrganizationInvite.generated';

const query = gql`
  mutation ResendOrganizationInvite($input: ResendOrganizationInviteInput!) {
    resendOrganizationInvite(input: $input)
  }
`;

export const useResendOrganizationInvite = (organizationId: string) => {
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation<
    ResendOrganizationInviteMutation,
    QueryError,
    ResendOrganizationInviteMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        ResendOrganizationInviteMutation,
        ResendOrganizationInviteMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      // Resending resets the expiry, so the row's isExpired flag may change.
      queryClient.invalidateQueries({
        queryKey: createOrganizationInvitesQueryKey(organizationId),
      });
    },
  });

  const resendInvite = (
    inviteId: string,
    options?: MutateOptions<
      ResendOrganizationInviteMutation,
      QueryError,
      ResendOrganizationInviteMutationVariables
    >
  ) => mutate({ input: { organizationId, inviteId } }, options);

  return {
    resendInvite,
    isPending,
  };
};
